import { TroySidebarMenu } from 'troy-portal';
import { Layout, theme } from '@duxton-web/ui';
import { FC, useEffect, useMemo, useState } from 'react';
import AppRoutes from 'src/routes';
import { useLocation, matchRoutes } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { routesConfig } from 'src/routes/routes';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '@components/ErrorFallback';
import { useIsMobile, useIsInWebView } from '@hooks/useDeviceType';
import { datadogRum } from '@datadog/browser-rum';
import { setSource, selectIsChopeCloud } from 'src/slices';
import { isLoggedInPlatform } from '@utils/index';
import { VIEWPORT_HEIGHT } from '@utils/viewport';

const { Content } = Layout;

// Troy owns the real widths via these CSS variables (troy-portal/sidebar/sidebar-v2/var.css);
// the constants are only a fallback for when the stylesheet has not been applied yet.
const SIDEBAR_WIDTH_EXPANDED_FALLBACK = 232;
const SIDEBAR_WIDTH_COLLAPSED_FALLBACK = 80;

const readSidebarWidth = (collapsed: boolean) => {
  const fallback = collapsed ? SIDEBAR_WIDTH_COLLAPSED_FALLBACK : SIDEBAR_WIDTH_EXPANDED_FALLBACK;
  const cssVariable = collapsed ? '--SIDEBAR_WIDTH_COLLAPSED' : '--SIDEBAR_WIDTH';
  const width = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue(cssVariable));
  return Number.isFinite(width) && width > 0 ? width : fallback;
};

const MexPortalLayout: FC = () => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const isMobile = useIsMobile();
  const isInWebView = useIsInWebView();
  const hideSidebar = isMobile || isInWebView;
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize after mobile detection is complete
    setIsInitialized(true);
  }, [hideSidebar]);

  const sidebarWidth = useMemo(() => readSidebarWidth(isSidebarCollapsed), [isSidebarCollapsed]);

  const location = useLocation();
  const dispatch = useDispatch();
  const matchedRoutes = matchRoutes(routesConfig, location.pathname);
  const currentRouteMeta = matchedRoutes?.[0]?.route?.meta;
  const isFromChopeCloud = useSelector(selectIsChopeCloud);

  // Sync `source` from URL to slice (only when URL has it; slice retains value across navigations)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const source = searchParams.get('source');
    if (source) {
      dispatch(setSource(source));
    }
  }, [location.search, dispatch]);

  const isSidebarHidden = useMemo(() => {
    return (
      !isLoggedInPlatform() ||
      currentRouteMeta?.isSidebarHidden === true ||
      (currentRouteMeta?.isChopeRoute === true && isFromChopeCloud)
    );
  }, [currentRouteMeta, isFromChopeCloud]);

  useEffect(() => {
    // TroySidebarMenu always mounts expanded, so a collapsed state must not outlive it
    if (!isInitialized || hideSidebar || isSidebarHidden) {
      setIsSidebarCollapsed(false);
    }
  }, [isInitialized, hideSidebar, isSidebarHidden]);

  return (
    <Layout style={{ minHeight: VIEWPORT_HEIGHT }}>
      {isInitialized && !hideSidebar && !isSidebarHidden && (
        <TroySidebarMenu trackTroyAnalytics={() => {}} IssliderCollpased={setIsSidebarCollapsed} />
      )}
      <Layout
        style={{
          height: VIEWPORT_HEIGHT,
          backgroundColor: 'white',
        }}
      >
        <Content
          style={{
            background: colorBgContainer,
            overflowY: 'scroll',
            height: '100%',
            marginLeft: !isInitialized || hideSidebar || isSidebarHidden ? 0 : sidebarWidth,
          }}
        >
          <ErrorBoundary
            fallback={<ErrorFallback />}
            onError={(error, info) => {
              datadogRum.addError(error, {
                type: 'ErrorBoundary',
                errorInfo: info,
              });
            }}
          >
            <AppRoutes />
          </ErrorBoundary>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MexPortalLayout;
